import React, { FC } from 'react';
import { ASD_ASSESSMENT_FEE, ASD_ASSESSMENT_REBATE } from '../utils/fee';

interface AsdPathwayCardProps {
  // Step 1 amount — the consultation fee the patient pays today.
  consultationFee: number;
  // Medicare or DVA card holder. Without a card there is no rebate, and the
  // psychiatrist assessment is described as having no upfront fee instead of
  // being bulk billed.
  hasMedicareCard: boolean;
}

const money = (amount: number) => `$${amount.toLocaleString('en-AU')}.00`;

/**
 * Combined + Metro fee pathway — shows that today's booking is the ADHD intake
 * only, with a downstream psychiatrist assessment (bulk billed for card
 * holders, no upfront fee otherwise) and an optional ASD assessment (which
 * attracts a Medicare rebate for card holders).
 */
const AsdPathwayCard: FC<AsdPathwayCardProps> = ({ consultationFee, hasMedicareCard }) => (
  <div className="mt-4 bg-white border border-blue-200 rounded-xl overflow-hidden">
    <div className="flex gap-2.5 items-start m-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs leading-relaxed text-amber-800">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#d97706" strokeWidth="1.9" className="flex-none mt-px" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 7.5h.01" strokeLinecap="round" />
      </svg>
      <div>
        Your current booking is for the ADHD intake only.
        {hasMedicareCard && <> No rebate applies to Step&nbsp;1.</>}
        {' '}
        If suitable, your clinician pre-books two psychiatrist sessions, an additional fee will apply.
      </div>
    </div>

    <div className="px-4 pb-1">
      <div className="flex gap-3 items-start py-3">
        <span className="flex-none w-9 h-9 rounded-lg flex items-center justify-center bg-blue-50 text-brand">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
            <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <span className="inline-block mb-1.5 px-2 py-0.5 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded">Step 1</span>
          <div className="text-sm font-bold text-brand-dark">ADHD intake (today)</div>
          <div className="mt-0.5 text-xs leading-snug text-slate-500">
            This is the appointment you are booking.
            {hasMedicareCard && ' No rebate applies.'}
          </div>
        </div>
        <div className="flex-none text-right">
          <span className="text-lg font-bold text-brand">{money(consultationFee)}</span>
        </div>
      </div>

      <div className="h-4 ml-4 border-l-2 border-dashed border-slate-200" aria-hidden="true" />

      <div className="flex gap-3 items-start py-3">
        <span className="flex-none w-9 h-9 rounded-lg flex items-center justify-center bg-blue-50 text-brand">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.6" />
            <path d="M5 20c0-3.7 3.1-6.2 7-6.2s7 2.5 7 6.2" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <span className="inline-block mb-1.5 px-2 py-0.5 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded">Step 2</span>
          <div className="text-sm font-bold text-brand-dark">Psychiatrist ADHD assessment</div>
          <div className="mt-0.5 text-xs leading-snug text-slate-500">
            {hasMedicareCard
              ? 'Completed after your ADHD intake. Bulk billed.'
              : 'Completed after your ADHD intake. No upfront fee.'}
          </div>
        </div>
        <div className="flex-none text-right">
          <span className="block text-[11px] font-semibold text-green-600">
            {hasMedicareCard ? 'Bulk billed' : 'No upfront fee'}
          </span>
          <span className="text-lg font-bold text-brand-dark">$0</span>
        </div>
      </div>

      <div className="h-4 ml-4 border-l-2 border-dashed border-slate-200" aria-hidden="true" />

      <div className="flex gap-3 items-start py-3">
        <span className="flex-none w-9 h-9 rounded-lg flex items-center justify-center bg-green-50 text-green-600">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 7h3a1.5 1.5 0 1 1 3 0h3v3a1.5 1.5 0 1 0 0 3v3h-3a1.5 1.5 0 1 0-3 0H4v-3a1.5 1.5 0 1 1 0-3Z" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <span className="inline-block mb-1.5 px-2 py-0.5 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded">Step 3</span>
          <div className="text-sm font-bold text-brand-dark">
            Additional ASD assessment
            {' '}
            <span className="font-semibold text-slate-500">(if recommended and you choose to proceed)</span>
          </div>
          <div className="mt-0.5 text-xs leading-snug text-slate-500">A fee applies.</div>
        </div>
        <div className="flex-none text-right">
          <span className="text-lg font-bold text-brand">
            +&nbsp;
            {money(ASD_ASSESSMENT_FEE)}
          </span>
        </div>
      </div>
    </div>

    {hasMedicareCard && (
    <div className="flex gap-2.5 items-start m-4 p-3 bg-green-50 border border-green-200 rounded-lg">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#059669" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-px" aria-hidden="true">
        <circle cx="12" cy="12" r="10" strokeWidth="1.6" />
        <path d="M8 12.5l2.5 2.5L16 9" strokeWidth="2.2" />
      </svg>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2.5 items-baseline">
          <span className="text-xs font-bold text-green-700">Estimated Medicare rebate:</span>
          <span className="text-xs font-bold text-green-700 whitespace-nowrap">
            From approximately
            {' '}
            {money(ASD_ASSESSMENT_REBATE)}
          </span>
        </div>
        <div className="mt-0.5 text-xs leading-snug text-green-800">
          Applies to Step&nbsp;3 fee only.
        </div>
      </div>
    </div>
    )}
  </div>
);

export default AsdPathwayCard;
